// Centralized route resolution + cardinality control.
//
// `window.location.pathname` is reported as the `route` label on most metrics.
// Without filtering, scanner probes (/.well-known/*, /_profiler/phpinfo,
// /.env, *.php) and source-leak probes (/src/...) explode label cardinality.
//
// We use a centralized DENYLIST that catches the obvious scanner patterns —
// no per-site config required. Anything matching a deny pattern collapses to
// '(unknown)' so the dashboard stays clean by default.
//
// Sites that need stricter precision (e.g., to bucket dynamic IDs like
// '/users/:id' or to exclude every non-canonical path) can opt into ALLOWLIST
// mode by passing `knownRoutes` to `initShopeepayReporter`. When provided, any
// path not in the list (after passing the denylist) is also treated as
// '(unknown)'. Routes can include `:placeholder` segments — a real visit to
// '/users/42' will be reported as '/users/:id'.
//
// Hash anchors (e.g. '/merchant#registration') are tracked DISTINCTLY ONLY when
// listed explicitly in `knownRoutes`. Otherwise the hash is dropped and the
// pathname-only entry wins. This keeps cardinality bounded while still letting
// marketing teams track external-link-driven anchor landings as their own
// route. (In-page anchor clicks do not currently fire a new pageView; that
// would require a `hashchange` listener — separate change.)

// ---------- denylist (always applied) ----------

const DEFAULT_DENY_PATTERNS = [
  /^\/\.well-known(\/|$)/i,                  // OIDC / OAuth discovery probes
  /^\/_profiler(\/|$)/i,                      // Symfony profiler probes
  /^\/wp-(admin|login|content|json|includes)(\/|$)/i, // WordPress probes
  /^\/(phpmyadmin|cpanel|administrator|manager)(\/|$)/i, // admin-panel probes
  /^\/\.(env|git|htaccess|aws|ssh|svn|hg)(\/|$)/i,    // config / dotfile probes
  /\.(php|asp|aspx|jsp|cgi|do)(\?|$|\/)/i,            // server-side script probes (we are SPA-only)
  /^\/(src|node_modules|dist|build)\//i,              // leaked source-path probes
  /^\/\/+/,                                            // multiple-slash garbage (e.g. ///*)
  /^\/undefined(\/|$)/i,                              // JS interpolation bug accidentally hitting telemetry
  /\.$/,                                               // trailing-dot paths — usually sentence-end punctuation autolinked into a URL (e.g. /ShopeePay.)
];

function matchesDeny(path) {
  for (const re of DEFAULT_DENY_PATTERNS) {
    if (re.test(path)) return true;
  }
  return false;
}

// ---------- optional per-site allowlist ----------

let exactRoutes = new Set();
let patternRoutes = []; // [{ regex, label }]

// Normalize a path string. Strips query (`?...`) and trailing slash.
// Hash (`#...`) is preserved — hash-anchored routes are meaningful when
// listed in the allowlist.
function normalizePath(path) {
  if (!path) return '/';
  let p = path.split('?')[0];
  if (p.length > 1 && !p.includes('#') && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function templateToRegex(template) {
  const regexStr = template
    .split('/')
    .map(seg => (seg.startsWith(':') ? '[^/]+' : escapeRegex(seg)))
    .join('/');
  return new RegExp('^' + regexStr + '$');
}

export function setKnownRoutes(routes) {
  exactRoutes = new Set();
  patternRoutes = [];
  if (!Array.isArray(routes) || routes.length === 0) return;
  for (const raw of routes) {
    if (typeof raw !== 'string') continue;
    const norm = normalizePath(raw);
    if (norm.includes('/:')) {
      patternRoutes.push({ regex: templateToRegex(norm), label: norm });
    } else {
      exactRoutes.add(norm);
    }
  }
}

export function getRoute() {
  if (typeof window === 'undefined') return '/';
  const path = normalizePath(window.location.pathname);
  const hash = window.location.hash || '';

  // Denylist runs against the pathname only — hash anchors never indicate
  // scanner activity.
  if (matchesDeny(path)) return '(unknown)';

  if (exactRoutes.size > 0 || patternRoutes.length > 0) {
    // Hash-anchored allowlist match wins when present — e.g.
    // '/merchant#registration' beats the generic '/merchant' if both are
    // listed. Falls through to pathname-only matching if no hashed entry
    // matches, so '/merchant#other-anchor' still reports as '/merchant'.
    if (hash) {
      const withHash = path + hash;
      if (exactRoutes.has(withHash)) return withHash;
    }
    if (exactRoutes.has(path)) return path;
    for (const { regex, label } of patternRoutes) {
      if (regex.test(path)) return label;
    }
    return '(unknown)';
  }

  // Non-allowlist mode: just the (denylist-cleaned) pathname. Hash is
  // intentionally dropped to keep cardinality bounded — sites that want
  // hash tracking must opt in via knownRoutes.
  return path;
}
